/**
 * @file リーダーの読み方 (ページで読む / 問題ごと) を URL から決める純関数である。
 *
 * @what: 資料が持つ枚数 (ページ・問) と URL の条件を受け、どちらの読み方で出すかを返す。
 *   片方しか無ければそれを出す。両方あるときはページで読むを既定にし、問題ごとを指す印
 *   (`view=items` と、問・単元・学校などの絞り込み) があるときだけ問題ごとにする。
 * @why: 本は本として読むのが既定で、問から引くのは目的を持って入る操作である (代表の判断、
 *   2026-09-12)。既定を入れ替えると、それまで「`?page=` が無い状態」が担っていた問題ごとの印が
 *   URL から無くなるので、`view=items` を明示の印として置く。絞り込みを持つリンク (計画の面・
 *   分析の面・段の面から渡ってくる `?unit=` `?school=` など) も問題ごととして扱う。
 *   ※なぜなら印を見ないと、絞り込みを渡されたのにページの面へ着地し、絞り込みが黙って
 *   無視されるためである。
 * @failure: 例外を投げない。ページも問も0件のときは問題ごとを返す (呼び出し側が取り込みなしの
 *   案内を出す)。
 */

/** 問題ごとで読むことを表す `?view=` の値である。 */
export const READER_VIEW_ITEMS = "items";

/**
 * 問題ごとを指す絞り込みの条件である (いずれかに値があれば問題ごとで出す)。
 *
 * どれも問の一覧にだけ適用できる条件なので、ページの面へ着地させない。
 */
const ITEM_FILTER_PARAMS = ["item", "unit", "tax", "school", "review", "solved"] as const;

export type ReaderMode = "items" | "pages";

/** `?page=` の連番である (数字でなければ null)。 */
export function readerPageParam(params: URLSearchParams): number | null {
  const raw = params.get("page");
  return raw !== null && /^\d+$/.test(raw) ? Number(raw) : null;
}

/** 読み方を決める。counts はその資料が持つページと問の数である。 */
export function readerModeOf(
  params: URLSearchParams,
  counts: { pages: number; items: number },
): ReaderMode {
  if (counts.pages === 0) return "items";
  if (counts.items === 0) return "pages";
  // ページを指しているものは、絞り込みより先に見る (共有されたページのリンク・つづきから)。
  if (readerPageParam(params) !== null) return "pages";
  if (params.get("view") === READER_VIEW_ITEMS) return "items";
  if (ITEM_FILTER_PARAMS.some((key) => (params.get(key) ?? "") !== "")) return "items";
  return "pages";
}
